import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Gift, Info } from 'lucide-react';
import { BottomSheet } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  bestOption,
  formatCrownDollars,
  humanizeValidity,
  newClientEventId,
  offerBonusCents,
  useClaimGift,
  type SurfaceItem,
} from '@/lib/monetization';
import { OfferTier } from './OfferTier';
import { scopeLabel } from './scope-label';

export interface SurfaceDetailSheetProps {
  /** The tapped surface, or `null` when the sheet is closed. */
  item: SurfaceItem | null;
  onClose: () => void;
}

/** Premium gradient pill shared by every primary action in the sheet. */
const CTA_CLASS =
  'mt-1 inline-flex h-12 w-full items-center justify-center gap-2 rounded-pill px-4 text-base font-semibold text-white shadow-[0_8px_20px_-8px_rgba(114,28,183,0.6)] transition-[filter,opacity] bg-[linear-gradient(135deg,var(--color-brand-grad-3)_0%,var(--color-brand-grad-2)_50%,var(--color-brand-grad-1)_100%)] hover:brightness-105 disabled:opacity-60';

/**
 * Bottom sheet opened when a compact {@link SurfaceCard} widget is tapped.
 * Hosts the full surface detail + the action (claim a gift / open the deposit
 * offer / read a message). Mirrors the v1.3 data wiring that used to live
 * inline on the card before the lobby was restyled to the compact promo widget.
 */
export function SurfaceDetailSheet({ item, onClose }: SurfaceDetailSheetProps) {
  // Keep the last shown surface mounted through the sheet's close animation so
  // its content doesn't blank out as the sheet slides down.
  const [shown, setShown] = useState<SurfaceItem | null>(item);
  if (item && item !== shown) setShown(item);

  return (
    <BottomSheet open={item != null} onClose={onClose}>
      {shown ? <Detail item={shown} onClose={onClose} /> : null}
    </BottomSheet>
  );
}

function Detail({ item, onClose }: { item: SurfaceItem; onClose: () => void }) {
  if (item.type === 'GIFT') return <GiftDetail item={item} />;
  if (item.type === 'OFFER') return <OfferDetail item={item} onClose={onClose} />;
  return <MessageDetail item={item} onClose={onClose} />;
}

/** Icon-in-gradient-chip + eyebrow + title row, shared by all three variants. */
function Hero({ icon, eyebrow, title }: { icon: React.ReactNode; eyebrow?: string; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--color-brand-grad-2)_0%,var(--color-brand-grad-1)_100%)] text-white shadow-[0_6px_16px_-6px_rgba(114,28,183,0.7)]">
        {icon}
      </span>
      <div className="min-w-0">
        {eyebrow && <span className="block text-[11px] font-bold uppercase tracking-wider text-purple/70">{eyebrow}</span>}
        <h2 className="text-lg font-bold leading-tight text-ink">{title}</h2>
      </div>
    </div>
  );
}

function GiftDetail({ item }: { item: SurfaceItem }) {
  const claim = useClaimGift();
  const gift = item.gift;
  const p = item.presentation ?? {};
  const title = p.title ?? p.headline ?? 'You have a gift';

  if (!gift) return null;

  const claimed = claim.isSuccess;
  // "Use within Xd / Xh / Xm" — prefix the shared validity bucket.
  const bucket = humanizeValidity(gift.expirySeconds);
  const validity = bucket && `Use within ${bucket}`;

  function handleClaim() {
    if (claim.isPending || claim.isSuccess) return;
    // useClaimGift already invalidates the surfaces + cash-back queries on
    // success; no per-call invalidation needed here.
    claim.mutate({
      experienceId: item.experienceId,
      data: { clientEventId: newClientEventId(), actionType: 'claim' },
    });
  }

  return (
    <div className="flex flex-col gap-4 pb-2">
      <Hero icon={<Gift className="size-5" />} eyebrow="Gift" title={title} />

      {/* Reward hero card — the amount is the headline. */}
      <div className="relative overflow-hidden rounded-card bg-[linear-gradient(135deg,var(--color-brand-grad-3)_0%,var(--color-brand-grad-2)_55%,var(--color-brand-grad-1)_100%)] px-5 py-5 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-10 size-32 rounded-full bg-surface/10 blur-2xl"
        />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">You received</span>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-4xl font-bold tracking-tight">
            <span aria-hidden className="mr-1 align-middle text-3xl">
              ◈
            </span>
            {formatCrownDollars(gift.amountCents)}
          </span>
          <span className="text-base font-medium text-white/85">CD</span>
        </div>
        <p className="mt-1 text-sm text-white/80">Crown Dollars · play on {scopeLabel(gift.scope)}.</p>
        {validity && (
          <span className="mt-3 inline-flex items-center gap-1 rounded-pill bg-surface/15 px-2.5 py-1 text-xs font-semibold text-white">
            <Clock className="size-3.5" /> {validity}
          </span>
        )}
      </div>

      {p.body && <p className="text-sm text-muted">{p.body}</p>}

      <button
        type="button"
        onClick={handleClaim}
        disabled={claim.isPending || claimed}
        className={cn(
          claimed
            ? 'mt-1 inline-flex h-12 w-full items-center justify-center gap-2 rounded-pill bg-success/15 px-4 text-base font-semibold text-success'
            : CTA_CLASS,
        )}
      >
        {claimed ? (
          <>
            <Check className="size-5" strokeWidth={3} /> Claimed
          </>
        ) : claim.isPending ? (
          'Claiming…'
        ) : (
          'Claim reward'
        )}
      </button>
      {claim.isError && <span className="text-sm text-danger-alt">Couldn&apos;t claim — try again.</span>}
    </div>
  );
}

function OfferDetail({ item, onClose }: { item: SurfaceItem; onClose: () => void }) {
  const navigate = useNavigate();
  const p = item.presentation ?? {};
  const options = [...(item.offer?.options ?? [])].sort((a, b) => a.position - b.position);
  const title = p.headline ?? p.title ?? 'Choose Your Boost';
  const subtitle = p.subtitle ?? p.body ?? 'Pick a pack and get extra Crown Dollars instantly';
  // Only tab a "Best Value" tier when there's a choice to make.
  const bestId = options.length > 1 ? bestOption(options)?.id : undefined;

  // The player picks a tier here, then carries it to the deposit page. Default
  // to the best-value tier (or the only one); the deposit screen no longer
  // offers selection — it just reflects the chosen tier's amount + bonus.
  const [selectedId, setSelectedId] = useState<string | undefined>(() => bestId ?? options[0]?.id);
  const selected = options.find((o) => o.id === selectedId);
  // CTA mirrors the chosen pack ("Deposit $100"); falls back only when an offer
  // somehow has no options to pick.
  const ctaLabel = selected ? `Deposit $${(selected.depositCents / 100).toLocaleString()}` : 'Go to deposit';

  function handleCta() {
    onClose();
    // Carry the full choice in the URL so the deposit screen can render the
    // bonus synchronously on first paint — surviving a refresh and shareable,
    // with no dependency on a follow-up fetch. The api still re-derives and
    // validates the bonus on mint, so these display values can't be exploited.
    if (selected) {
      const search = new URLSearchParams({
        tab: 'deposit',
        offer: item.experienceId,
        option: selected.id,
        amount: String(selected.depositCents),
        bonus: String(offerBonusCents(selected)),
      });
      navigate({ pathname: '/wallet', search: `?${search}` });
    } else {
      navigate('/wallet?tab=deposit');
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-2">
      <div className="flex flex-col items-center gap-1 text-center">
        <h2 className="text-2xl font-medium leading-tight text-ink">{title}</h2>
        <p className="text-base text-ink">{subtitle}</p>
      </div>

      {options.length > 0 && (
        <div className="flex flex-col gap-5">
          {options.map((o) => (
            <OfferTier
              key={o.id}
              option={o}
              best={o.id === bestId}
              selected={o.id === selectedId}
              onSelect={() => setSelectedId(o.id)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col items-center gap-2">
        <button type="button" onClick={handleCta} disabled={options.length > 0 && !selected} className={CTA_CLASS}>
          {ctaLabel}
        </button>
        {selected && (
          <p className="text-center text-base font-medium text-purple">
            + Get {formatCrownDollars(offerBonusCents(selected))} Crown Dollars
          </p>
        )}
      </div>
    </div>
  );
}

function MessageDetail({ item, onClose }: { item: SurfaceItem; onClose: () => void }) {
  const p = item.presentation ?? {};
  const title = p.title ?? p.headline ?? 'Update';
  const body = p.body ?? p.subtitle;

  return (
    <div className="flex flex-col gap-4 pb-2">
      <Hero icon={<Info className="size-5" />} title={title} />
      {body && <p className="text-sm text-muted">{body}</p>}
      <button type="button" onClick={onClose} className={CTA_CLASS}>
        Got it
      </button>
    </div>
  );
}
